/**
 * RolesList Component
 *
 * Displays all roles in a grid layout with cards.
 * Uses real API data via useRoleManagement hook.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Grid,
  Alert,
  CircularProgress,
  Box,
} from '@mui/material';
import { RoleCard } from './RoleCard';
import { RoleFormDialog, type RoleFormData } from './RoleFormDialog';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { useRoleManagement, useUpdateRole, useSetRolePermissions, useDeleteRole, type Role } from '@/hooks';
import { useNotify } from '@/hooks/useNotify';
import { useAuthStore } from '@/stores/authStore';
import { describeApiError } from '@/lib/describeApiError';

const MANAGE_ROLES_PERMISSION = 'settings.manage_roles';

export function RolesList() {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const hasPermission = useAuthStore((s) => s.hasPermission);

  const { roles, isLoading, isError, error } = useRoleManagement();

  const updateRole = useUpdateRole();
  const setRolePermissions = useSetRolePermissions();
  const deleteRole = useDeleteRole();

  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [deletingRole, setDeletingRole] = useState<Role | null>(null);

  const handleEdit = (role: Role) => {
    if (!hasPermission(MANAGE_ROLES_PERMISSION)) return;
    setEditingRole(role);
  };

  const handleSaveEdit = (formData: RoleFormData) => {
    if (!editingRole || !hasPermission(MANAGE_ROLES_PERMISSION)) return;

    // Update metadata
    updateRole.mutate(
      {
        roleId: editingRole.id,
        data: {
          name: formData.name,
          description: formData.description,
          // Level is hidden in the workshop form; keep the stored value (#2668).
          level: editingRole.level,
        },
      },
      {
        onSuccess: () => {
          if (!hasPermission(MANAGE_ROLES_PERMISSION)) return;
          // Then update permissions
          setRolePermissions.mutate(
            {
              roleId: editingRole.id,
              permissions: formData.permissionIds,
            },
            {
              onSuccess: () => {
                notify.success(t('rolesManagement.roleSaved'), t('rolesManagement.roleSavedDesc', { name: formData.name }));
                setEditingRole(null);
              },
              onError: (err) => {
                notify.error(t('rolesManagement.saveFailed'), describeApiError(err, tc, { fallback: tc('errors.generic') }));
              },
            }
          );
        },
        onError: (err) => {
          notify.error(t('rolesManagement.saveFailed'), describeApiError(err, tc, { fallback: tc('errors.generic') }));
        },
      }
    );
  };

  const handleDelete = (role: Role) => {
    if (!hasPermission(MANAGE_ROLES_PERMISSION)) return;
    if (role.isSystem) {
      notify.error(t('rolesManagement.cannotDelete'), t('rolesManagement.systemRoleCannotDelete'));
      return;
    }
    setDeletingRole(role);
  };

  const handleConfirmDelete = () => {
    if (!deletingRole || !hasPermission(MANAGE_ROLES_PERMISSION)) return;

    deleteRole.mutate(deletingRole.id, {
      onSuccess: () => {
        notify.success(t('rolesManagement.roleDeleted'), t('rolesManagement.roleDeletedDesc', { name: deletingRole.name }));
        setDeletingRole(null);
      },
      onError: (err) => {
        notify.error(t('rolesManagement.deleteFailed'), describeApiError(err, tc, { fallback: tc('errors.generic') }));
        setDeletingRole(null);
      },
    });
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Alert severity="error">
        {t('rolesManagement.loadFailed', { error: describeApiError(error, tc, { fallback: tc('errors.generic') }) })}
      </Alert>
    );
  }

  return (
    <>
      <Grid container spacing={3}>
        {roles.map((role) => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={role.id}>
            <RoleCard role={role} onEdit={handleEdit} onDelete={handleDelete} />
          </Grid>
        ))}
      </Grid>

      {roles.length === 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          {t('rolesManagement.noRolesFound')}
        </Alert>
      )}

      {/* Edit Role Dialog */}
      {editingRole && (
        <RoleFormDialog
          open={!!editingRole}
          role={editingRole}
          onClose={() => setEditingRole(null)}
          onSave={handleSaveEdit}
          saving={updateRole.isPending || setRolePermissions.isPending}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={!!deletingRole}
        title={t('rolesManagement.deleteRole')}
        message={t('rolesManagement.deleteConfirm', { name: deletingRole?.name })}
        confirmText={tc('delete')}
        confirmColor="error"
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeletingRole(null)}
      />
    </>
  );
}
